import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Shield, Eye, EyeOff, Loader2, CheckCircle } from 'lucide-react';

function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [done, setDone] = useState(false);
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    const init = async () => {
      // PKCE: exchange code if present
      const code = searchParams.get('code');
      if (code) {
        const { error } = await supabase.auth.exchangeCodeForSession(code);
        if (error) {
          console.error('Recovery code exchange failed:', error);
          toast.error('This reset link has expired or is invalid.');
          navigate('/auth');
          return;
        }
        setReady(true);
        return;
      }

      // Implicit flow: tokens in hash (Supabase auto-processes)
      const hash = window.location.hash;
      if (hash && hash.includes('access_token')) {
        // Give Supabase client time to process hash
        await new Promise((r) => setTimeout(r, 500));
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          setReady(true);
          return;
        }
      }

      // Fallback: wait and check
      await new Promise((r) => setTimeout(r, 1000));
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        setReady(true);
        return;
      }

      toast.error('No valid recovery session found.');
      navigate('/auth');
    };

    init();
  }, []);

  const handleReset = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) {
        toast.error(error.message || 'Failed to reset password');
        return;
      }
      setDone(true);
      toast.success('Password updated successfully!');
      // Sign out the recovery session so user can login fresh
      await supabase.auth.signOut();
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f7f7f7] flex flex-col">
      <nav className="bg-white border-b border-[#e5e5e5]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-14">
            <a href="/" className="flex items-center gap-2.5">
              <Shield className="w-7 h-7 text-brand" strokeWidth={2.2} />
              <span className="text-lg font-bold text-[#222]">Sergeant TAP</span>
            </a>
          </div>
        </div>
      </nav>
      <div className="flex-1 flex items-center justify-center py-12 px-4">
        <Card className="w-full max-w-md p-8 border border-[#e0e0e0] shadow-sm bg-white" data-testid="reset-password-card">
          {!ready && !done && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand mx-auto mb-4" />
              <p className="text-[#666]">Verifying reset link...</p>
            </div>
          )}

          {ready && !done && (
            <form onSubmit={handleReset} className="space-y-5">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-[#222]">Set New Password</h2>
                <p className="text-[#666] mt-2">Enter your new password below</p>
              </div>
              <div>
                <Label className="text-[#333] font-medium">New Password</Label>
                <div className="relative mt-1.5">
                  <Input data-testid="reset-password-input"
                    type={showPassword ? 'text' : 'password'} value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-11 pr-12 border-[#ddd]" placeholder="Min 8 characters" required />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#aaa] hover:text-[#666]">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-[#333] font-medium">Confirm Password</Label>
                <Input data-testid="reset-confirm-input" type="password" value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="h-11 mt-1.5 border-[#ddd]" placeholder="Re-enter password" required />
              </div>
              <Button data-testid="reset-submit-btn" type="submit" disabled={loading}
                className="w-full h-11 bg-brand hover:bg-brand-hover text-white btn-uppercase">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
          )}

          {done && (
            <div className="text-center py-4" data-testid="reset-success-view">
              <div className="w-16 h-16 bg-brand-light rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-brand" />
              </div>
              <h2 className="text-2xl font-bold text-[#222] mb-3">Password Reset Complete</h2>
              <p className="text-[#666] mb-6">Your password has been updated. You can now sign in with your new password.</p>
              <Button onClick={() => navigate('/auth')}
                className="bg-brand hover:bg-brand-hover text-white btn-uppercase"
                data-testid="go-to-login-btn">
                Sign In
              </Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
